import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { last, range } from 'lodash';
import { Dialog, DialogType, DialogFooter } from 'office-ui-fabric-react/lib/Dialog';
import { PrimaryButton, DefaultButton } from 'office-ui-fabric-react/lib/Button';
import { DatePicker as FluentDatePicker } from 'office-ui-fabric-react/lib/DatePicker';
import { Calendar } from 'office-ui-fabric-react/lib/Calendar';
import theme from '../../theme.style';
import { pxToRem } from '../../utils';
import styles from './ContractTermsForm.style';

const dialogContentProps = {
  type: DialogType.normal,
  title: 'Select Effective Date',
};

const formatDate = date => moment.utc(new Date(date)).local().format('MM/DD/YYYY');

const ContractDateSelectionDialog = ({
  isOpen,
  currencyType,
  value,
  statementType,
  onDismiss,
  onConfirm,
  setEffectiveDate,
  quarterStartMonths,
  ...other
}) => {
  const [errorMessage, setErrorMessage] = useState('');

  let minDate = moment().startOf('month').toDate();

  if (statementType === 'quarter' && !quarterStartMonths.includes(moment(minDate).month())) {
    const minDateMonth = moment(minDate).month();
    const quarterStart = quarterStartMonths.find(
      (x, index) => x < minDateMonth && quarterStartMonths[index + 1] > minDateMonth,
    );
    if (quarterStart) {
      minDate = moment().set('month', quarterStart).startOf('month').toDate();
    } else {
      [minDate] = range(1, 3)
        .map(val => {
          const d = moment(minDate).subtract(val, 'months').startOf('month').toDate();
          return last(quarterStartMonths) === moment(d).month() ? d : '';
        })
        .filter(e => !!e);
    }
  }

  let datePickerStyles;
  if (errorMessage) {
    datePickerStyles = {
      root: {
        selectors: {
          '& .ms-TextField-fieldGroup': {
            borderColor: theme.palette.accentError,
            borderWidth: pxToRem(1),
            backgroundColor: theme.palette.neutralWhite,
          },
        },
      },
    };
  }

  const checkForError = dateValue => {
    const dateMonth = moment(dateValue).month();
    if (statementType === 'quarter' && !quarterStartMonths.includes(dateMonth)) {
      const quarterMonths = quarterStartMonths.map(mon => moment().month(mon).format('MMM'));
      setErrorMessage(
        `Selected month must be at the beginning of a quarter (${quarterMonths.join(
          ', ',
        )}). Please reselect.`,
      );
    } else if (new Date(dateValue) < minDate) {
      setErrorMessage('Please select the month/ quarter for Open statement.');
    } else {
      setErrorMessage('');
    }
  };

  const handleClick = () => {
    setErrorMessage('');
  };

  useEffect(() => {
    if (!value) {
      setEffectiveDate(minDate);
    }
  }, []);

  useEffect(() => {
    if (value) {
      checkForError(value);
    }
  }, [value]);

  return (
    <Dialog hidden={!isOpen} dialogContentProps={dialogContentProps} {...other}>
      Please select the month/ quarter of available open statements for currency change. By default
      the 1st date of selected month/ quarter will be defaulted to implement the{' '}
      {currencyType === 'contractCurrency' ? 'Contract' : 'Payment'} Currency Change.
      <div className={styles.floatRight}>
        <FluentDatePicker
          data-testid="contractDateSelection"
          className={styles.datePicker}
          placeholder="MM/DD/YYYY"
          ariaLabel="Effective Date"
          formatDate={formatDate}
          calendarAs={props => (
            <Calendar
              {...props}
              isDayPickerVisible={false}
              isMonthPickerVisible
              value={minDate}
              minDate={minDate}
              showGoToToday={false}
            />
          )}
          styles={datePickerStyles}
          textField={{ errorMessage }}
          value={new Date(value || minDate)}
          onSelectDate={selectedDate => {
            setEffectiveDate(selectedDate);
          }}
          disableAutoFocus
        />
      </div>
      <DialogFooter>
        <DefaultButton
          text="Cancel"
          onClick={() => {
            handleClick();
            onDismiss();
          }}
        />
        <PrimaryButton
          text="Confirm"
          onClick={() => {
            handleClick();
            onConfirm();
          }}
          disabled={!value || errorMessage}
        />
      </DialogFooter>
    </Dialog>
  );
};

ContractDateSelectionDialog.propTypes = {
  isOpen: PropTypes.bool,
  currencyType: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  statementType: PropTypes.string,
  onDismiss: PropTypes.func,
  onConfirm: PropTypes.func,
  setEffectiveDate: PropTypes.func,
  quarterStartMonths: PropTypes.arrayOf(PropTypes.any),
};

ContractDateSelectionDialog.defaultProps = {
  isOpen: false,
  currencyType: '',
  value: null,
  statementType: '',
  onDismiss: null,
  onConfirm: null,
  setEffectiveDate: () => {},
  quarterStartMonths: [],
};

export default ContractDateSelectionDialog;
